import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Icon } from '@liminal/web-ui/icons/Icon';
import type { IconName } from '@liminal/web-ui/icons/registry';

// Staff guide to Partner Rewards. Every rule here is one partner-service
// enforces (product/rewards/partner_service/README.md); the alerts' own advice
// is in prometheus.rules, group rewards-partner.

const STEPS = [
  { icon: 'send', title: 'A customer pays a partner by Pix', detail: "Any Pix payment to the partner's CNPJ or Pix key counts." },
  { icon: 'sparkles', title: 'They choose in the app', detail: 'Earn LIL, or pay with LIL and get the money back.' },
] as const satisfies readonly { icon: IconName; title: string; detail: string }[];

const OUTCOMES = [
  { icon: 'gift', title: 'Earn', detail: "LIL goes into their rewards, at the partner's earn rate." },
  {
    icon: 'cash',
    title: 'Pay with LIL',
    detail: 'Their LIL goes to the treasury, and the treasury sends them the payment back in dollars (USDC).',
  },
] as const satisfies readonly { icon: IconName; title: string; detail: string }[];

const SETUP = [
  {
    icon: 'wallet',
    title: 'Treasury',
    detail: 'A Safe on Base that holds USDC. Every Pay with LIL is paid back from it. A payment it cannot cover cannot be paid with LIL.',
  },
  {
    icon: 'stats-chart',
    title: 'Budget',
    detail: 'A cap: a total, and a most per payment. Pausing or closing a budget stops both choices at its partners.',
  },
  {
    icon: 'people',
    title: 'Partner',
    detail: 'The merchant: how its payments are recognised (CNPJ, Pix keys), its two rates and Pay with LIL levels, and its budget.',
  },
] as const satisfies readonly { icon: IconName; title: string; detail: string }[];

const NEEDS_YOU = [
  {
    signal: 'Free to pay back is low',
    meaning: 'The treasury is close to refusing Pay with LIL.',
    action: 'Top up USDC on the treasury Safe.',
  },
  {
    signal: 'Needs review',
    meaning: 'The payment was refunded after the customer chose, so LIL or money had already moved. Nothing is taken back automatically.',
    action: 'Decide each one by hand.',
  },
  {
    signal: 'Money owed too long',
    meaning: 'The customer gave their LIL but has not had their money back.',
    action: "Ask an engineer to look. The payment's details have the ids they need.",
  },
] as const;

function IconTile({ icon }: { readonly icon: IconName }) {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: `${borderRadius.md}px`,
        bgcolor: webColors.surface.secondary,
      }}
    >
      <Icon name={icon} size={20} color={webColors.text.secondary} />
    </Box>
  );
}

function Item({ icon, title, detail }: { readonly icon: IconName; readonly title: string; readonly detail: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <IconTile icon={icon} />
      <Stack spacing={0.25}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </Stack>
    </Stack>
  );
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Card variant="elevated">
      <Stack spacing={2}>
        <Typography variant="h6">{title}</Typography>
        {children}
      </Stack>
    </Card>
  );
}

/** How Partner Rewards works, for staff: the flow, what to set up, how rates and levels read, and what needs a person. */
export function HowItWorksPage() {
  return (
    <Stack spacing={3} sx={{ maxWidth: 960 }}>
      <Section title="The flow">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr auto 1.4fr' }, gap: 2, alignItems: 'center' }}>
          {STEPS.map((step) => (
            <Box key={step.title} sx={{ display: 'contents' }}>
              <Item icon={step.icon} title={step.title} detail={step.detail} />
              <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <Icon name="arrow-forward" size={20} color={webColors.text.tertiary} />
              </Box>
            </Box>
          ))}
          <Stack spacing={1.5} sx={{ pl: { md: 2 }, borderLeft: { md: `2px solid ${webColors.border.light}` } }}>
            {OUTCOMES.map((outcome) => (
              <Item key={outcome.title} icon={outcome.icon} title={outcome.title} detail={outcome.detail} />
            ))}
          </Stack>
        </Box>
      </Section>

      <Section title="What you set up">
        <Typography variant="body2" color="text.secondary">
          In this order: a partner needs a budget, and a budget is only useful once the treasury holds money.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {SETUP.map((item, index) => (
            <Item key={item.title} icon={item.icon} title={`${index + 1}. ${item.title}`} detail={item.detail} />
          ))}
        </Box>
      </Section>

      <Section title="How rates work">
        <Typography variant="body2">
          A partner has two rates, both in LIL per $1. The <strong>pay rate</strong> is what a customer spends to get $1 back.
          The <strong>earn rate</strong> is what they get per $1 instead, and is always lower.
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: `${borderRadius.md}px`,
            bgcolor: webColors.surface.secondary,
          }}
        >
          <Typography variant="body2">
            At 100 and 5, a $20.40 payment costs 2,040 LIL to pay back, or earns 102 LIL. Earning is worth 5% of the
            payment: that is its cashback.
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          A rate change only applies to payments made after it. Each change is kept in the partner&apos;s history.
        </Typography>
      </Section>

      <Section title="Pay with LIL levels">
        <Typography variant="body2">
          A partner&apos;s rates can also carry three <strong>levels</strong>, each an amount in dollars. Paying with LIL
          then takes one level rather than the whole payment: the customer gets that level&apos;s dollars back, never more
          than they paid, for the level&apos;s dollars at the pay rate in LIL. A partner with no levels keeps Pay with LIL
          on the whole payment.
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: `${borderRadius.md}px`,
            bgcolor: webColors.surface.secondary,
          }}
        >
          <Typography variant="body2">
            A payment offers every level below it, and the first level that reaches it. At 100 with levels of $10, $20
            and $30, a $15 payment offers 1,000 LIL for $10 back or 2,000 LIL for all $15, and not $30.
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Set a partner&apos;s levels only once the app build with the level picker is live: until then the app cannot pay
          with LIL at a partner that has them. The partner form asks for levels on a new partner and on any rate change,
          so hold both until then.
        </Typography>
      </Section>

      <Section title="When something needs you">
        <Stack>
          {NEEDS_YOU.map((row) => (
            <Box
              key={row.signal}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '200px 1fr 1fr' },
                gap: { xs: 0.5, md: 2 },
                py: 1.5,
                borderTop: `1px solid ${webColors.border.light}`,
              }}
            >
              <Typography variant="subtitle2">{row.signal}</Typography>
              <Typography variant="body2" color="text.secondary">
                {row.meaning}
              </Typography>
              <Typography variant="body2">{row.action}</Typography>
            </Box>
          ))}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          The Overview tab shows all three. Click a count to see those payments.
        </Typography>
      </Section>
    </Stack>
  );
}
